import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router";
import wsRealtimeService from "utils/wsRealtimeService";

const realtimeService = wsRealtimeService;

// A custom hook that builds on useLocation to parse the query string for you.

// Probably will add more functions in the future so disabling default
// export check until then. Remove these comments once another util
// function is added in this file.
/* eslint-disable import/prefer-default-export */
function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

function useSelectionItem(defaultValue) {
  const [valueSelected, setValue] = useState(defaultValue);

  const handleChangeSelection = (event) => {
    setValue(event.target.value);
  };
  return { valueSelected, handleChangeSelection };
}

function usePagination(defaultPageSize = 20) {
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: defaultPageSize,
  });
  const [totalCount, setTotalCount] = useState(0);

  return {
    paginationModel,
    setPaginationModel,
    totalCount,
    setTotalCount,
  };
}

function useLoading() {
  const [loading, setLoading] = useState(false);
  const handleSetLoading = (status) => setLoading(status);

  return { handleSetLoading, loading, setLoading };
}

function useModal() {
  const [isModal, setIsModal] = useState(false);

  const openModal = () => setIsModal(true);
  const closeModal = () => setIsModal(false);
  return { isModal, openModal, closeModal };
}

// Decodes the base64url-encoded payload section of a JWT without a library.
function decodeTokenPayload(token) {
  try {
    const b64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(b64));
  } catch {
    return null;
  }
}

/**
 * Subscribes to a single Oura realtime measurement topic and returns the latest
 * message pushed by the backend.
 *
 * @param {string} deviceId        - Oura device UUID
 * @param {string} measurementType - One of: "hr", "spo2", "temp", "hrv"
 * @returns {{ latestMessage: object|null }}
 */
function useOuraRealtime(deviceId, measurementType) {
  const [latestMessage, setLatestMessage] = useState(null);
  const [orgId, setOrgId] = useState(null);

  // Resolve org_id once by decoding the IoT JWT returned by the credentials endpoint.
  // Staff users have no org_id — use "+" wildcard to match any org.
  useEffect(() => {
    wsRealtimeService
      .getCredentials()
      .then((creds) => {
        const payload = decodeTokenPayload(creds.token);
        if (payload?.is_staff) setOrgId("+");
        else if (payload?.org_id) setOrgId(payload.org_id);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!orgId || !deviceId || !measurementType) return;
    const topic = `stel/oura/realtime/org/${orgId}/${deviceId}/${measurementType}`;
    return realtimeService.subscribe(topic, setLatestMessage); // eslint-disable-line consistent-return
  }, [orgId, deviceId, measurementType]);

  return { latestMessage };
}

/**
 * Subscribes to the realtime topic for hub/general measurements and returns a
 * counter that increments on each new measurement event.
 *
 * - Staff users (org_id is null, is_staff is true): subscribe to
 *   `stel/measurements/realtime/org/+/all` (wildcard = all orgs).
 * - Org users: subscribe to `stel/measurements/realtime/org/{org_id}/all`.
 *
 * @returns {{ latestMeasure: object|null }}
 */
function useMeasureRealtime(onMessage) {
  const [topicInfo, setTopicInfo] = useState(null);

  const onMessageRef = useRef(onMessage);
  useEffect(() => {
    onMessageRef.current = onMessage;
  });

  useEffect(() => {
    wsRealtimeService
      .getCredentials()
      .then((creds) => {
        const payload = decodeTokenPayload(creds.token);
        if (payload?.is_staff) {
          setTopicInfo({ topic: "stel/measurements/realtime/org/+/all" });
        } else if (payload?.org_id) {
          setTopicInfo({
            topic: `stel/measurements/realtime/org/${payload.org_id}/all`,
          });
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!topicInfo) return;
    const handleMessage = (message) => {
      if (onMessageRef.current) {
        onMessageRef.current(message);
      }
    };
    return wsRealtimeService.subscribe(topicInfo.topic, handleMessage); // eslint-disable-line consistent-return
  }, [topicInfo]);
}

/**
 * Subscribes to the realtime topic for a single hub's measurements.
 *
 * Topic: `stel/measurements/realtime/hubs/{hub_id}`
 *
 * @param {string} hubId
 * @returns {{ latestHubMeasure: object|null }}
 */
function useHubMeasureRealtime(hubId) {
  const [latestHubMeasure, setLatestHubMeasure] = useState(null);
  const [topic, setTopic] = useState(null);
  useEffect(() => {
    wsRealtimeService
      .getCredentials()
      .then((creds) => {
        const payload = decodeTokenPayload(creds.token);
        if (payload?.is_staff) {
          setTopic(`stel/measurements/realtime/org/+/hubs/${hubId}`);
        } else if (payload?.org_id) {
          setTopic(
            `stel/measurements/realtime/org/${payload.org_id}/hubs/${hubId}`
          );
        }
      })
      .catch(() => {});
  }, [hubId]);

  useEffect(() => {
    if (!hubId || !topic) return;
    return realtimeService.subscribe(topic, setLatestHubMeasure); // eslint-disable-line consistent-return
  }, [hubId, topic]);

  return { latestHubMeasure };
}

export {
  useQuery,
  useSelectionItem,
  usePagination,
  useLoading,
  useModal,
  useOuraRealtime,
  useMeasureRealtime,
  useHubMeasureRealtime,
};
